import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import type { MapRef } from 'react-map-gl';
import {
  QueryMap,
  type BBox,
  type SpatialQuery,
} from 'components/query-map/query-map';
import {
  containRect,
  mimeTypeFor,
  resolveOutputSize,
  type ChartImageExporter,
  type ExportImageOptions,
} from 'components/chart-export';
import {
  useRegisterChartExport,
  type ChartExportDescriptor,
} from './chart-export-registry';
import { LtdSlotLayer } from './ltd-slot-layer';
import { SlotLayer } from './slot-layer';
import { TractLayer } from './tract-layer';
import { UnitLayer } from './unit-layer';
import { ScopedMapLayerToggles } from './scoped-layer-toggles';
import { useFilteredMapBounds, usePackageBounds } from './hooks';
import { useEval3Page } from './context';
import {
  aoiEditingAtom,
  locatedRowAtom,
  mapConfigAtom,
  mapRefAtom,
  spatialFilterAtom,
} from './atoms';
import styles from './package-map.module.scss';

const DEFAULT_LATITUDE = 31;
const DEFAULT_LONGITUDE = -98;
const DEFAULT_ZOOM = 4;

// JPEG quality for the map image export (PNG ignores it).
const MAP_JPEG_QUALITY = 0.95;

// This map's content-export identity — its PNG lands in the ZIP as `charts[id]`.
const MAP_EXPORT_DESCRIPTOR: ChartExportDescriptor = {
  id: 'map',
  name: 'Map',
  group: 'map',
};

/**
 * A content-export capturer over the LIVE Mapbox canvas — the registry needs only an
 * `exportToBlob`, so that's all this provides. It rasterizes the current map view (the map is
 * created with `preserveDrawingBuffer` so the WebGL buffer stays readable).
 *
 * The selected aspect ratio IS honored (INS-10099). The target frame is resolved from the
 * export options via the shared `resolveOutputSize` (the same source of truth the chart
 * exporters use: `16:9 → 1920×1080`, `custom → W×H`, `current → the live size`), then the
 * whole live map is fit inside it — CONTAIN: no crop, no distortion — and the letterbox /
 * pillarbox remainder is painted white. White matches the chart exporters' background (so map
 * and charts look consistent in the ZIP) and keeps JPEG — which has no alpha — from filling the
 * bars black. `current` (or an unresolved custom size, e.g. an empty field) leaves the capture
 * WYSIWYG. Element size is not applied to a map; the output format is honored.
 *
 * @param {MapRef} mapRef - The react-map-gl handle for the live map.
 * @return {Pick<ChartImageExporter, 'exportToBlob'>} A capturer that rasterizes the map canvas.
 */
function createMapExporter(
  mapRef: MapRef
): Pick<ChartImageExporter, 'exportToBlob'> {
  return {
    exportToBlob: (opts: ExportImageOptions) =>
      new Promise((resolve) => {
        const mimeType = mimeTypeFor(opts.outputFormat);
        const quality =
          opts.outputFormat === 'jpeg' ? MAP_JPEG_QUALITY : undefined;

        const source = mapRef.getMap().getCanvas();
        // DEVICE pixels (backing-store size = CSS × devicePixelRatio), deliberately NOT the
        // CSS box: the WYSIWYG short-circuit below and `drawImage(source, …)` both operate at
        // the canvas's native resolution, so the `target === liveSize` reshape test must also
        // be in device pixels — otherwise a `custom` request equal to the CSS size would
        // short-circuit to a device-resolution capture instead of the size the user asked for.
        const liveSize = { width: source.width, height: source.height };
        // The target frame for the selected aspect ratio; fall back to the live size for
        // `current` or an unresolved custom size. A degenerate (zero-sized) live canvas also
        // routes to the WYSIWYG capture below rather than dividing by zero when scaling.
        const target = resolveOutputSize(opts, liveSize) ?? liveSize;
        const reshape =
          liveSize.width > 0 &&
          liveSize.height > 0 &&
          (target.width !== liveSize.width ||
            target.height !== liveSize.height);

        if (!reshape) {
          source.toBlob((blob) => resolve(blob), mimeType, quality);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          // No 2D context (shouldn't happen) — fall back to the raw WYSIWYG capture.
          source.toBlob((blob) => resolve(blob), mimeType, quality);
          return;
        }

        // Paint the letterbox / pillarbox ground white (see the function JSDoc).
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, target.width, target.height);

        // CONTAIN: fit the whole live map inside the target frame (no crop, no distortion),
        // centered — the remainder stays as the white bars painted above.
        const { dx, dy, width, height } = containRect(liveSize, target);
        ctx.drawImage(source, dx, dy, width, height);

        canvas.toBlob((blob) => resolve(blob), mimeType, quality);
      }),
  };
}

/**
 * Debounce (ms) on the spatial-query emit. In Visible-Extent mode the viewport
 * polygon would otherwise emit on every `moveend`; in AOI mode a vertex drag fires
 * `draw.update` continuously. Debouncing coalesces those into one
 * `spatialFilterAtom` write (→ one refetch) once the gesture settles.
 */
const SPATIAL_QUERY_DEBOUNCE_MS = 500;

/**
 * The Master Data map: a `QueryMap` rendering the three package-scoped layers
 * (Unit / Tract / Slot) with the layers panel's "Scoped" toggles and camera framing
 * driven by the pinned row. Its **spatial-query** capability is wired to the Global
 * Map Filter mode (`spatialFilterAtom`): Use Visible Map Extent reports the viewport
 * as a polygon (`'bounds'`), Draw AOI Polygon lets the user draw one (`'polygon'`),
 * and Package Area runs a plain map (no query). The captured polygon flows into
 * `spatialFilterAtom` → `mapScopeAtom`, which narrows the table / summary / map (the
 * consumers read it in P2-T26). The emit is debounced so a continuous pan or
 * vertex-drag coalesces into one update.
 *
 * It subscribes to `locatedRowAtom` (pin → camera framing + highlight) and
 * `spatialFilterAtom` (the spatial query) — both map-only state, so a pin click or a
 * pan re-renders only the map, never the sibling `MasterDataPanel` (the heavy table;
 * its views re-fetch off `mapScopeAtom` in their own hook). It clears the pinned row
 * on unmount so a stale pin doesn't carry across page navigations (the atom is
 * module-scoped).
 *
 * @return {JSX.Element} The composed QueryMap with its scoped layers.
 */
export function PackageMap() {
  // Business-heavy container: read the page identity once and push it down to the
  // scoped layers (thin) as props + into `usePackageBounds` as a param.
  const { packageId, calculationId } = useEval3Page();
  // The package's own bbox — the map's default framing when nothing is pinned or filtered.
  const packageBounds = usePackageBounds(packageId);
  // The filtered highlight's extent (map_elements bbox) — set when a dataset filter
  // or spatial scope narrows the map, so the camera zooms/recenters to the narrowed
  // features like a pin (INS-10376). null when nothing narrows.
  const filteredBounds = useFilteredMapBounds(packageId, calculationId);

  const [located, dispatchLocated] = useAtom(locatedRowAtom);
  useEffect(() => () => dispatchLocated({ type: 'clear' }), [dispatchLocated]);

  // Keep the Mapbox canvas sized to its container. mapbox-gl@2's `trackResize`
  // only listens to WINDOW resize — it has no container ResizeObserver (upstream
  // declined it, mapbox-gl-js#9083). So any layout change that resizes the map
  // container WITHOUT resizing the window — the Eval Settings panel collapsing
  // beside it, the left-pane divider drag, or the Layout-Settings show/hide —
  // never redraws the canvas, leaving a grey gap on the reclaimed side. We observe
  // the map's OWN container (PackageMap renders QueryMap and owns no wrapper div, so
  // `getContainer()` is the element to watch — no extra ref needed) and resize on
  // every size change; this also tracks the panel's 240ms width animation
  // frame-by-frame. Same fix as map-query's MapSection, which observes a wrapper ref.
  const mapRef = useAtomValue(mapRefAtom);
  useEffect(() => {
    const map = mapRef?.getMap();
    const container = map?.getContainer();
    if (!map || !container) return undefined;
    const observer = new ResizeObserver(() => map.resize());
    observer.observe(container);
    return () => observer.disconnect();
  }, [mapRef]);

  // Register the map's image exporter for content export while the map is live (mapRef set),
  // and unregister on teardown. Capture pulls the current canvas at Export-click time.
  const onMapExporterReady = useRegisterChartExport(MAP_EXPORT_DESCRIPTOR);
  useEffect(() => {
    onMapExporterReady(mapRef ? createMapExporter(mapRef) : null);
    return () => onMapExporterReady(null);
  }, [mapRef, onMapExporterReady]);

  // Global Map Filter spatial query. Reading mode + polygon here re-renders
  // PackageMap on a pan / draw, but that stays map-local (MasterDataPanel doesn't
  // subscribe to spatialFilterAtom — its table re-fetches off mapScopeAtom, P2-T26).
  const [{ mode: spatialMode, polygon }, dispatchSpatial] =
    useAtom(spatialFilterAtom);

  // Controlled AOI vertex-editing flag — set by the panel's "Edit", mirrored back from
  // the map's draw mode (so it also tracks native click-to-edit and clears on finish).
  const [aoiEditing, setAoiEditing] = useAtom(aoiEditingAtom);

  // Stable sink for the captured viewport / AOI polygon (debounced inside QueryMap).
  const onSpatialPolygonChange = useCallback(
    (next: GeoJSON.Polygon | null) =>
      dispatchSpatial({ type: 'setPolygon', polygon: next }),
    [dispatchSpatial]
  );

  // The QueryMap spatial-query bundle from the Global Map Filter mode: Visible
  // Extent → report the viewport (`'bounds'`); Draw AOI → draw a polygon
  // (`'polygon'`, controlled by the stored polygon); Package Area → no query (plain
  // map). The capture lands in spatialFilterAtom → mapScopeAtom.
  const query = useMemo<SpatialQuery | undefined>(() => {
    if (spatialMode === 'extent') {
      return {
        mode: 'bounds',
        onChange: onSpatialPolygonChange,
        debounceMs: SPATIAL_QUERY_DEBOUNCE_MS,
      };
    }
    if (spatialMode === 'aoi') {
      return {
        mode: 'polygon',
        polygon,
        onChange: onSpatialPolygonChange,
        editing: aoiEditing,
        onEditingChange: setAoiEditing,
        debounceMs: SPATIAL_QUERY_DEBOUNCE_MS,
      };
    }
    return undefined;
  }, [spatialMode, polygon, aoiEditing, setAoiEditing, onSpatialPolygonChange]);

  // Camera framing, in precedence order. This is the SOLE owner of the camera policy:
  // `useFilteredMapBounds` just returns the narrowed bbox as data; whether to FRAME to
  // it (or to leave the camera put) is decided here.
  //   1. pinned row(s) — an explicit "show on map" always wins (a pinned row with no
  //      bbox still highlights, it just doesn't move the camera → drop undefineds); a
  //      pin is a deliberate action, so it moves the camera even in Visible-Extent mode.
  //   2. Visible-Extent mode — NEVER auto-frame: the viewport IS the scope, so the
  //      camera must stay wherever the user (or the prior mode) left it. Sits ABOVE the
  //      filtered + package branches so neither can move the camera: without it the
  //      package fallback would snap the map back out to the whole package the instant
  //      Visible Extent is selected (the filtered branch goes empty as the filter's
  //      bbox stops mattering), discarding the filtered zoom-in. Framing to the bbox
  //      here would also LOOP (fit → moveend → recapture a larger viewport → refit).
  //      Returning `[]` = "don't fit" (same as the not-yet-loaded case).
  //   3. the filtered highlight extent — a dataset filter / drawn AOI zooms the map to
  //      the narrowed features, mirroring a pin (INS-10376). Unreachable in extent mode
  //      (caught by #2 above), so its bbox is used only where framing is wanted.
  //   4. the whole package extent — the default, and where clearing the filter flies
  //      back to (empty until its bbox loads / when the package has none).
  const fitToBounds = useMemo(() => {
    if (located.length > 0) {
      return located
        .map((row) => row.bbox)
        .filter((bbox): bbox is BBox => Boolean(bbox));
    }
    if (spatialMode === 'extent') {
      return [];
    }
    if (filteredBounds) {
      return [filteredBounds];
    }
    return packageBounds ? [packageBounds] : [];
  }, [located, filteredBounds, packageBounds, spatialMode]);

  return (
    <QueryMap
      className={styles.map}
      latitude={DEFAULT_LATITUDE}
      longitude={DEFAULT_LONGITUDE}
      zoom={DEFAULT_ZOOM}
      mapConfigAtom={mapConfigAtom}
      refAtom={mapRefAtom}
      // Keep the WebGL buffer readable so content export can rasterize the live map canvas
      // (`getCanvas().toBlob`); scoped to this map — other QueryMap consumers are unaffected.
      preserveDrawingBuffer
      showControls
      showMapFilters
      scopedLayers={<ScopedMapLayerToggles />}
      fitToBounds={fitToBounds}
      query={query}
    >
      <UnitLayer packageId={packageId} calculationId={calculationId} />
      <TractLayer packageId={packageId} calculationId={calculationId} />
      {/* LTD places under the drilled wells: a planned location should never obscure real
          geometry when the two overlap (INS-10669). */}
      <LtdSlotLayer packageId={packageId} calculationId={calculationId} />
      <SlotLayer packageId={packageId} calculationId={calculationId} />
    </QueryMap>
  );
}
